// SchemaObject.js
import { formatISO, formatISODuration, intervalToDuration } from 'date-fns';

import {
  getPoster,
  getAssetManifest,
  getAssetTrailer,
  getUserPicture,
  MediaAssetFormat,
  MediaPreset,
} from '../resource/getMediaAsset.js';

import faviconPngSource from '../../img/favicon.png';

/**
 * get coporation
 * @return {Object}
 */
export const getCoporation = () => {
  const logoImageSource = `https://swag.live${faviconPngSource}`;
  return {
    '@type': 'Corporation',
    '@id': 'https://swag.live/#corporation',
    name: 'SWAG',
    url: 'https://swag.live',
    logo: {
      '@type': 'ImageObject',
      '@id': 'https://swag.live/#logo',
      url: logoImageSource,
      contentUrl: logoImageSource,
      width: 512,
      height: 512,
      caption: 'SWAG',
    },
    image: {
      '@id': 'https://swag.live/#logo',
    },
    sameAs: [
      'https://zh.wikipedia.org/zh-tw/Swag',
      'https://twitter.com/swaglive_app',
      'https://www.instagram.com/swag_taiwan/',
    ],
  };
};

/**
 * get interaction counter object
 * @param {string} {type} - type (ex: 'BuyAction', 'LikeAction')
 * @param {number} {count} - action count
 * @return {Object}
 */
export const getInteractionCounter = ({ type, count }) => {
  return {
    '@type': 'InteractionCounter',
    interactionType: type.startsWith('https')
      ? type
      : `https://schema.org/${type}`,
    userInteractionCount: Math.floor(count),
  };
};

/**
 * get person object
 * @param {string} {id} - user id
 * @param {string} {username} - username
 * @param {string} {biography} - biography
 * @param {array} {hasOfferCatalog} - item list of video objects
 * @return {Object}
 */
export const getPerson = ({ id, username, biography, hasOfferCatalog }) => {
  return {
    '@type': 'Person',
    '@id': `https://swag.live/user/${id}#person`,
    url: `https://swag.live/user/${id}`,
    name: username,
    description: biography,
    sameAs: username ? [`https://swag.live/u/${username}`] : undefined,
    image: {
      '@type': 'ImageObject',
      '@id': `https://swag.live/user/${id}#avatar`,
      url: getUserPicture({
        userId: id,
        size: 128,
        format: MediaAssetFormat.JPG,
      }).href,
      width: 128,
      height: 128,
    },
    hasOfferCatalog,
  };
};

/**
 * get flix video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @return {Object}
 */
export const getFlixVideoObject = ({
  id,
  assetId,
  title,
  description,
  durationSecs,
  author,
  postedAtUnix,
  isFree,
  interactionStatistic,
  encodings,
  associatedMedia,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const serialCode = title?.replace(/^\[([^[\]]*)\].*/, '$1');
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/flix/${id}#video-hls`,
    url: `https://swag.live/flix/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '1024x1024',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: MediaPreset.SD,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    keywords: [serialCode, 'SWAG'].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    encodings,
    associatedMedia,
  };
};

/**
 * get flix preview video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @return {Object}
 */
export const getFlixPreviewVideoObject = ({
  id,
  assetId,
  title,
  description,
  durationSecs = 10,
  author,
  postedAtUnix,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/flix/${id}#preview`,
    url: `https://swag.live/flix/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '1024x1024',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetTrailer({
      id,
      assetId,
      format: MediaAssetFormat.MP4,
      preset: MediaPreset.SD,
      duration: durationSecs,
    }),
    encodingFormat: 'video/mp4',
    author,
    uploadDate,
    isAccessibleForFree: true,
    associatedMedia: {
      '@id': `https://swag.live/flix/${id}#video-hls`,
    },
  };
};

/**
 * get flix hls video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {bool} {isFree}
 * @return {Object}
 */
export const getFlixHlsVideoObject = ({
  id,
  assetId,
  title,
  description,
  durationSecs,
  author,
  postedAtUnix,
  isFree,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/flix/${id}#video-hls`,
    url: `https://swag.live/flix/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '1024x1024',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: MediaPreset.SD,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    encodesCreativeWork: {
      '@id': `https://swag.live/flix/${id}#video-dash`,
    },
  };
};

/**
 * get story video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {username} - to compose story title
 * @param {string} {caption} - caption
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} [{encodings}]
 * @param {object} [{associatedMedia}]
 * @param {bool} {isFree}
 * @return {Object}
 */
export const getStoryVideoObject = ({
  id,
  assetId,
  username,
  caption,
  durationSecs = 10,
  author,
  postedAtUnix,
  isFree,
  interactionStatistic,
  encodings,
  associatedMedia,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const name = `${username} - SWAG`;
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/story/${id}#video-hls`,
    url: `https://swag.live/story/${id}`,
    name,
    description: caption || name,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '1024x1024',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: MediaPreset.SD,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    keywords: [username, 'SWAG'],
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    encodings,
    associatedMedia,
  };
};

/**
 * get story preview video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {username} - to compose story title
 * @param {string} {caption} - caption
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @return {Object}
 */
export const getStoryPreviewVideoObject = ({
  id,
  assetId,
  username,
  caption,
  durationSecs = 10,
  author,
  postedAtUnix,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const name = `${username} - SWAG`;
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/story/${id}#preview`,
    url: `https://swag.live/story/${id}`,
    name,
    description: caption || name,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '1024x1024',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: MediaPreset.SD_PREVIEW,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    author,
    uploadDate,
    isAccessibleForFree: true,
    associatedMedia: {
      '@id': `https://swag.live/story/${id}#video-hls`,
    },
  };
};

/**
 * get post video object
 * @param {string} {id} - message id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @return {Object}
 */
export const getPostVideoObject = ({
  id,
  title,
  description,
  durationSecs,
  author,
  username,
  postedAtUnix,
  isFree,
  interactionStatistic,
  encodings,
  associatedMedia,
  thumbnailUrl,
  contentUrl,
  hashtags = [],
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const serialCode = title?.replace(/^\[([^[\]]*)\].*/, '$1');
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/post/${id}#video-hls`,
    url: `https://swag.live/post/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl,
    contentUrl,
    encodingFormat: 'application/vnd.apple.mpegurl',
    keywords: [serialCode, username, ...hashtags, 'SWAG'].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    encodings,
    associatedMedia,
  };
};

/**
 * get post preview video object
 * @param {string} {id} - message id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @return {Object}
 */
export const getPostPreviewVideoObject = ({
  id,
  title,
  description,
  durationSecs = 10,
  author,
  postedAtUnix,
  thumbnailUrl,
  contentUrl,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/post/${id}#preview`,
    url: `https://swag.live/post/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl,
    contentUrl,
    encodingFormat: 'application/vnd.apple.mpegurl',
    author,
    uploadDate,
    isAccessibleForFree: true,
    associatedMedia: {
      '@id': `https://swag.live/post/${id}#video-hls`,
    },
  };
};

/**
 * get short schema object
 * @param {string} {id} - message id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @return {Object}
 */
export const getShortSchemaObject = ({
  id,
  title,
  description,
  durationSecs,
  author,
  username,
  postedAtUnix,
  isFree,
  interactionStatistic,
  encodings,
  associatedMedia,
  thumbnailUrl,
  contentUrl,
  hashtags = [],
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `https://swag.live/shorts/${id}#video-hls`,
    url: `https://swag.live/shorts/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl,
    contentUrl,
    encodingFormat: 'application/vnd.apple.mpegurl',
    keywords: [username, ...hashtags, 'SWAG'].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    encodings,
    associatedMedia,
  };
};
